import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlarmClock, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SlaBadge } from '@/components/sla';
import { AreaBadge, PriorityLabel, StatusBadge } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/form-field';
import { Card, Input, Select, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Paginated, TicketListItem, TicketMeta } from '@/lib/types';
import { cn, formatRelative } from '@/lib/utils';

export function useTicketMeta() {
  return useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), staleTime: 60_000 });
}

const FILTER_KEYS = ['q', 'state', 'areaId', 'assignee', 'priority', 'teamId', 'statusId', 'followUpDue', 'sort', 'customerId', 'watching'] as const;

export function TicketsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const page = Number(params.get('page') ?? 1);
  const { data: meta } = useTicketMeta();

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== (params.get('q') ?? '')) set('q', search);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = new URLSearchParams({ page: String(page), pageSize: '25' });
  for (const k of FILTER_KEYS) {
    const v = params.get(k);
    if (v) query.set(k, v);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', 'list', query.toString()],
    queryFn: () => api<Paginated<TicketListItem>>(`/tickets?${query}`),
    placeholderData: keepPreviousData,
  });

  const quick = [
    { label: 'Offen', apply: { state: '', assignee: '', followUpDue: '', watching: '' } },
    { label: 'Mir zugewiesen', apply: { state: '', assignee: 'me', followUpDue: '', watching: '' } },
    { label: 'Ich bin beteiligt', apply: { state: '', assignee: '', followUpDue: '', watching: 'me' } },
    { label: 'Nicht zugewiesen', apply: { state: '', assignee: 'unassigned', followUpDue: '', watching: '' } },
    { label: 'Wiedervorlage fällig', apply: { state: '', assignee: '', followUpDue: 'true', watching: '' } },
    { label: 'Alle', apply: { state: 'all', assignee: '', followUpDue: '', watching: '' } },
  ];
  const activeQuick = quick.findIndex((q) => Object.entries(q.apply).every(([k, v]) => (params.get(k) ?? '') === v));

  return (
    <>
      <PageHeader
        title="Tickets"
        description="Anfragen, Störungen und Aufträge"
        actions={
          can('tickets.create') && (
            <Button asChild>
              <Link to="/tickets/new">
                <Plus /> Neues Ticket
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap gap-1.5">
        {quick.map((q, i) => (
          <button
            key={q.label}
            onClick={() => {
              const next = new URLSearchParams(params);
              Object.entries(q.apply).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
              next.delete('page');
              setParams(next, { replace: true });
            }}
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              i === activeQuick ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {q.label}
          </button>
        ))}
      </div>
      <Card>
        <div className="grid gap-2 border-b p-3 sm:grid-cols-2 lg:grid-cols-[1fr_repeat(4,minmax(0,11rem))]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8" placeholder="Nummer, Betreff, Kunde …" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Suche" />
          </div>
          <Select value={params.get('areaId') ?? ''} onChange={(e) => set('areaId', e.target.value)} aria-label="Bereich">
            <option value="">Alle Bereiche</option>
            {meta?.areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select value={params.get('statusId') ?? ''} onChange={(e) => set('statusId', e.target.value)} aria-label="Status">
            <option value="">Alle Status</option>
            {meta?.statuses
              .filter((s) => s.scope === 'TICKET')
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            <optgroup label="Rückfragen der Buchhaltung">
              {meta?.statuses
                .filter((s) => s.scope === 'BILLING_QUERY')
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </optgroup>
          </Select>
          <Select value={params.get('priority') ?? ''} onChange={(e) => set('priority', e.target.value)} aria-label="Priorität">
            <option value="">Alle Prioritäten</option>
            <option value="URGENT">Dringend</option>
            <option value="HIGH">Hoch</option>
            <option value="NORMAL">Normal</option>
            <option value="LOW">Niedrig</option>
          </Select>
          <Select value={params.get('sort') ?? ''} onChange={(e) => set('sort', e.target.value)} aria-label="Sortierung">
            <option value="">Letzte Aktivität</option>
            <option value="created">Neueste zuerst</option>
            <option value="priority">Priorität</option>
            <option value="due">Fälligkeit</option>
          </Select>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Ticket</TH>
              <TH className="hidden md:table-cell">Kunde</TH>
              <TH>Status</TH>
              <TH className="hidden lg:table-cell">Bearbeiter</TH>
              <TH className="hidden sm:table-cell">Aktivität</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={5}>
                  <Skeleton className="h-40" />
                </TD>
              </TR>
            )}
            {data?.items.length === 0 && <EmptyRow colSpan={5}>Keine Tickets für diese Auswahl.</EmptyRow>}
            {data?.items.map((t) => {
              const followUpDue = t.followUpAt && new Date(t.followUpAt) <= new Date();
              return (
                <TR key={t.id} className="cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                  <TD className="max-w-md">
                    <div className="flex items-center gap-2">
                      <PriorityLabel priority={t.priority} compact />
                      <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                      {followUpDue && <AlarmClock className="size-3.5 text-amber-600" aria-label="Wiedervorlage fällig" />}
                    </div>
                    <Link to={`/tickets/${t.id}`} className="mt-0.5 block truncate font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                      {t.subject}
                    </Link>
                    <div className="mt-1">
                      <AreaBadge name={t.area.name} color={t.area.color} />
                    </div>
                  </TD>
                  <TD className="hidden md:table-cell">
                    {t.customer ? <span className="truncate">{t.customer.name}</span> : <span className="text-muted-foreground">{t.requesterName ?? '–'}</span>}
                  </TD>
                  <TD>
                    <div className="flex flex-col items-start gap-1">
                      <StatusBadge name={t.status.name} color={t.status.color} />
                      <SlaBadge t={t} />
                    </div>
                  </TD>
                  <TD className="hidden lg:table-cell">{t.assignee?.name ?? <span className="text-muted-foreground">–</span>}</TD>
                  <TD className="hidden whitespace-nowrap text-muted-foreground sm:table-cell">{formatRelative(t.lastActivityAt)}</TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
        {data && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => {
              const next = new URLSearchParams(params);
              next.set('page', String(p));
              setParams(next);
            }}
          />
        )}
      </Card>
    </>
  );
}
